import { localizedCountryName } from '@foundation/countries';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { ChangeEvent, ReactNode, useState } from 'react';

import {
  ArrowIcon,
  disableUIBlocker,
  enableUIBlocker,
  PageLayout,
  SimpleButton,
} from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { triggerErrorToast } from '@/libs/toasts';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import { activateMandate } from '@/services/payments/instruments/PaymentInstrumentsApi';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';

import { SepaMandateConsentDetails } from './SepaMandateConsentDetails';

const styles = {
  container: 'flex gap-4 relative focused-container items-start',
  checkbox: 'mt-2 ml-1 text-12 scale-125',
};

const joinParts = (...parts: ReadonlyArray<string | undefined>) =>
  parts.filter(Boolean).join(' ');

function Row({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <tr>
      <th
        scope="row"
        className="table-cell text-left font-normal"
      >
        {label}
      </th>
      <td className="table-cell">{children}</td>
    </tr>
  );
}

export function SepaMandateActivationPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { accessToken } = useAuth();
  const [consentChecked, setConsentChecked] = useState(false);

  const activation = PaymentStore.mandateActivation;
  if (activation === undefined) {
    throw new Error('mandate activation details are missing');
  }

  const {
    mandateReference,
    details: { mandate, mandators },
  } = activation;

  const onConfirm = () => {
    enableUIBlocker();
    activateMandate(mandateReference, accessToken ?? '')
      .then(() => {
        // Reload so the stored instruments list refetches and the status toast shows.
        // The deep-link param is dropped, otherwise the activation page would reopen.
        const url = new URL(window.location.href);
        url.searchParams.delete('mandate');
        url.searchParams.set('status', RedirectStatus.Success);
        window.location.assign(url.toString());
      })
      .catch((err: Error) => {
        disableUIBlocker();
        TelemetryService.trackException(err, 'SepaMandateActivationPage');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'Sepa Mandate activation failed',
          err,
        );
        triggerErrorToast(
          t('Something went wrong!'),
          t('Failed to activate mandate.'),
        );
      });
  };

  return (
    <PageLayout>
      <div className="m-sm md:m-md">
        <div>
          <SimpleButton
            onClick={() =>
              NavigationService.navigateTo({
                page: InternalPage.StoredInstruments,
              })
            }
          >
            <span className={'mt-2 flex flex-row items-center justify-center'}>
              <ArrowIcon direction="left" />
              <span className={'ml-xs'}>{t('Back')}</span>
            </span>
          </SimpleButton>
        </div>
        <br />
        <b>{t('Account Holder')}</b>
        <table>
          <tbody>
            <Row label={t('First name/Last Name')}>
              {mandate.accountHolderName ||
                joinParts(mandate.cst_FirstName, mandate.cst_LastName)}
            </Row>
            <Row label={t('Customer Number')}>{mandate.customerId}</Row>
            <Row label={t('Email')}>{PaymentStore.customerEmail}</Row>
            <Row label={t('Street and house number')}>
              {joinParts(mandate.cst_Street, mandate.cst_StreetNumber)}
            </Row>
            <Row label={t('ZIP/City')}>
              {joinParts(mandate.cst_PostalCode, mandate.cst_City)}
            </Row>
            <Row label={t('Country')}>
              {mandate.cst_Country &&
                localizedCountryName(mandate.cst_Country, language)}
            </Row>
            <Row label={t('IBAN')}>{mandate.iban}</Row>
          </tbody>
        </table>
        {mandators.map((mandator) => (
          <div key={mandator.mandatorNumber}>
            <br />
            <b>{t('Payee')}</b>
            <table>
              <tbody>
                <Row label={t('Company')}>{mandator.name}</Row>
                <Row label={t('Creditor ID')}>{mandator.creditorId}</Row>
                <Row label={t('Street and house number')}>
                  {mandator.streetFull}
                </Row>
                <Row label={t('ZIP/City')}>
                  {joinParts(mandator.postalCode, mandator.city)}
                </Row>
                <Row label={t('Country')}>
                  {mandator.country &&
                    localizedCountryName(mandator.country, language)}
                </Row>
                <Row label={t('Mandate type')}>
                  {t('SEPA basic mandate for repeated use')}
                </Row>
              </tbody>
            </table>
          </div>
        ))}
        <br />
        <div className={styles.container}>
          <input
            type="checkbox"
            id="activationConsentCheckbox"
            className={styles.checkbox}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setConsentChecked(event.target.checked)
            }
          />
          <label htmlFor="activationConsentCheckbox">
            <div
              className={`overflow-y-auto ${
                consentChecked ? 'h-auto' : 'h-36'
              } `}
            >
              <SepaMandateConsentDetails
                legalEntity={mandate.assignedMandators[0]}
              />
            </div>
          </label>
        </div>
        <br />
        <div className={'flex flex-row items-center justify-center wrap'}>
          <PayButton
            title={t('Activate')}
            disabled={!consentChecked}
            onClick={onConfirm}
          />
        </div>
      </div>
    </PageLayout>
  );
}
